import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import {
  useGetExtractionHistoryQuery,
  type ExtractionHistoryItem,
} from "@/services/tax-closing";

const VIEW_DATA_PATH = "/tax/closing";

const MONTHS_PT: Record<number, string> = {
  1: "Janeiro",
  2: "Fevereiro",
  3: "Março",
  4: "Abril",
  5: "Maio",
  6: "Junho",
  7: "Julho",
  8: "Agosto",
  9: "Setembro",
  10: "Outubro",
  11: "Novembro",
  12: "Dezembro",
};

interface CacheStatusVisual {
  label: string;
  className: string;
}

const CACHE_STATUS_VISUAL: Record<"available" | "expired", CacheStatusVisual> = {
  available: {
    label: "Disponível",
    className:
      "bg-foreground/5 text-foreground border-transparent hover:bg-foreground/5",
  },
  expired: {
    label: "Expirado",
    className:
      "bg-transparent text-muted-foreground border border-border hover:bg-transparent",
  },
};

const PAGE_LIMIT = 10;

export function ExtractionHistoryTable() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useGetExtractionHistoryQuery(
    { page, limit: PAGE_LIMIT },
    { refetchOnMountOrArgChange: true },
  );

  const entries = data?.data ?? [];
  const meta = data?.meta;

  return (
    <Card className="h-full p-5 flex flex-col gap-4">
      <header className="border-b pb-3">
        <h2 className="text-sm font-semibold text-foreground">
          Histórico de extrações
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Extrações anteriores com cache disponível para visualização
        </p>
      </header>
      <HistoryTableBody isLoading={isLoading} entries={entries} />
      {meta && meta.totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={meta.totalPages}
          total={meta.total}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => p + 1)}
        />
      )}
    </Card>
  );
}

// --------------------------------------------------------------------
// Body — loading / empty / table
// --------------------------------------------------------------------
interface HistoryTableBodyProps {
  isLoading: boolean;
  entries: ExtractionHistoryItem[];
}

function HistoryTableBody({ isLoading, entries }: HistoryTableBodyProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-1.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Nenhuma extração realizada ainda.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
            <th className="py-2 pr-3 font-medium">Data/Hora</th>
            <th className="py-2 pr-3 font-medium">Período</th>
            <th className="py-2 pr-3 font-medium">Lojas</th>
            <th className="py-2 pr-3 font-medium">Usuário</th>
            <th className="py-2 pr-3 font-medium">Status</th>
            <th className="py-2 pr-3 font-medium text-right">Ações</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {entries.map((entry) => (
            <HistoryRow key={entry.id} entry={entry} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --------------------------------------------------------------------
// Single row
// --------------------------------------------------------------------
interface HistoryRowProps {
  entry: ExtractionHistoryItem;
}

function HistoryRow({ entry }: HistoryRowProps) {
  const navigate = useNavigate();
  const storeLabel =
    entry.stores.length === 1 ? "1 loja" : `${entry.stores.length} lojas`;

  return (
    <tr className="hover:bg-muted/30">
      <td className="py-2 pr-3 tabular-nums">
        {new Date(entry.createdAt).toLocaleString("pt-BR")}
      </td>
      <td className="py-2 pr-3">
        {MONTHS_PT[entry.month]} {entry.year}
      </td>
      <td className="py-2 pr-3 tabular-nums">{storeLabel}</td>
      <td className="py-2 pr-3 text-muted-foreground truncate max-w-[220px]">
        {entry.userEmail}
      </td>
      <td className="py-2 pr-3">
        <CacheStatusBadge cacheAvailable={entry.cacheAvailable} />
      </td>
      <td className="py-2 pr-3 text-right">
        {entry.cacheAvailable && entry.jobId ? (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5"
            onClick={() => navigate(`${VIEW_DATA_PATH}/${entry.jobId}`)}
          >
            <Eye className="h-3.5 w-3.5" />
            Ver dados
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}

// --------------------------------------------------------------------
// Status badge
// --------------------------------------------------------------------
function CacheStatusBadge({ cacheAvailable }: { cacheAvailable: boolean }) {
  const visual = cacheAvailable
    ? CACHE_STATUS_VISUAL.available
    : CACHE_STATUS_VISUAL.expired;
  return <Badge className={visual.className}>{visual.label}</Badge>;
}

// --------------------------------------------------------------------
// Pagination
// --------------------------------------------------------------------
interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}

function Pagination({
  page,
  totalPages,
  total,
  onPrev,
  onNext,
}: PaginationProps) {
  return (
    <div className="flex items-center justify-between pt-4">
      <span className="text-sm text-muted-foreground">
        Página {page} de {totalPages} ({total} registros)
      </span>
      <div className="flex gap-1">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          disabled={page <= 1}
          onClick={onPrev}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          disabled={page >= totalPages}
          onClick={onNext}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
